import { useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Options = {
  configurationType: ConfigurationType
  // Klucz tłumaczenia nazwy zakładki, np. 'configuration-menu.timesheet-additional'.
  tabLabelKey: string
  queryKeys: string[]
}

/** Przywraca ustawienia domyślne zakładki. Zapis idzie do bazy od razu. */
export function useRestoreDefaultSettingsMutation(
  companyId: string,
  { configurationType, tabLabelKey, queryKeys }: Options,
) {
  const queryClient = useQueryClient()
  const { t } = useLocalize()
  const tabLabel = t(tabLabelKey).toString()

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post(
        `/api/${companyId}/configuration/restoreDefaultConfiguration`,
        { configurationType },
      )
      return data
    },

    onError: (error) => {
      console.error('Error restoring default settings:', error)
      toast.error({ text: t('configuration.save-error').toString(), autoCloseMs: 8000 })
    },

    onSuccess: () => {
      queryKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key, companyId] }),
      )
      toast.success({
        title: t('configuration-menu.settings-restored-title').toString(),
        text: t('configuration-menu.settings-restored-details')
          .toString()
          .replace('{0}', tabLabel),
      })
    },
  })

  const confirmMessage = `${t('configuration-menu.warning-message-first-part')} ${tabLabel} ${t(
    'configuration-menu.warning-message-second-part',
  )}`

  return { ...mutation, confirmMessage }
}
